import { useLang } from "@/lib/i18n";
import { IMG, PROCESS } from "@/lib/images";
import { FadeIn, MaskLines, SectionHead } from "@/components/Reveal";
import { PrimaryBtn } from "@/components/Buttons";
import CtaBand from "@/components/CtaBand";

const STEP_ICONS = [
  PROCESS.inventaire,
  PROCESS.offres,
  PROCESS.logistique,
  PROCESS.controle,
  PROCESS.paiement,
  PROCESS.recyclage,
];

export default function Partnerships() {
  const { t } = useLang();
  const c = t.partnerships;

  return (
    <div>
      <section className="relative overflow-hidden pb-14 pt-8 lg:pt-12" data-testid="partnerships-hero">
        <div className="pointer-events-none absolute inset-0 bg-[radial-gradient(50%_55%_at_80%_0%,rgba(14,124,90,0.1),transparent_70%)]" />
        <div className="relative mx-auto max-w-7xl px-5 sm:px-8">
          <FadeIn y={14}>
            <div className="flex items-center gap-3">
              <span className="rounded-none bg-brandtint px-3 py-1.5 text-[11px] font-bold uppercase tracking-[0.18em] text-branddark">
                {c.eyebrow}
              </span>
            </div>
          </FadeIn>
          <MaskLines
            lines={c.titleLines}
            accent={1}
            className="mt-6 max-w-4xl font-display text-4xl font-bold tracking-[-0.02em] sm:text-5xl lg:text-[3.4rem] lg:leading-[1.14]"
          />
          <FadeIn y={14} delay={0.5}>
            <p className="mt-7 max-w-2xl text-base font-medium leading-relaxed text-ink2 sm:text-lg">
              {c.body}
            </p>
            <p className="mt-10 text-[11px] font-bold uppercase tracking-[0.2em] text-ink3">
              {c.audienceLabel}
            </p>
            <div className="mt-4 flex flex-wrap gap-2.5">
              {c.audience.map((a) => (
                <span
                  key={a}
                  className="rounded-none border border-line bg-white px-4 py-2 text-xs font-semibold text-ink2 shadow-sm transition-colors duration-200 hover:border-brand/50 hover:text-brand"
                >
                  {a}
                </span>
              ))}
            </div>
          </FadeIn>
        </div>
      </section>

      <section className="pb-16 pt-8" data-testid="partnerships-image">
        <div className="mx-auto max-w-7xl px-5 sm:px-8">
          <FadeIn>
            <div className="relative">
              <span
                aria-hidden
                className="absolute -bottom-4 -right-4 hidden h-full w-full rounded-none border-2 border-brand/20 sm:block"
              />
              <div className="relative overflow-hidden rounded-none shadow-card">
                <img
                  src={IMG.ewaste}
                  alt="Démantèlement de composants électroniques"
                  className="h-[300px] w-full object-cover lg:h-[420px]"
                  loading="lazy"
                />
                <div className="absolute inset-x-0 bottom-0 h-28 bg-gradient-to-t from-black/45 to-transparent" />
                <span className="absolute bottom-4 left-4 rounded-none bg-white/95 px-3 py-1.5 text-[10px] font-bold uppercase tracking-[0.16em] text-ink2">
                  {c.eyebrow}
                </span>
              </div>
            </div>
          </FadeIn>
        </div>
      </section>

      <section className="border-t border-line bg-white py-24 lg:py-32" data-testid="partnerships-steps">
        <div className="mx-auto max-w-7xl px-5 sm:px-8">
          <SectionHead eyebrow={c.eyebrow} title={c.stepsTitle} />
          <div className="mt-14 grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
            {c.steps.map((s, i) => (
              <FadeIn key={i} delay={0.05 * (i % 3)}>
                <div
                  className="h-full rounded-none border border-line bg-paper p-8 transition-all duration-300 hover:-translate-y-1 hover:shadow-card"
                  data-testid={`step-card-${i}`}
                >
                  <div className="flex items-center justify-between">
                    <img src={STEP_ICONS[i]} alt={s.title} className="h-12 w-12 object-contain" loading="lazy" />
                    <span className="rounded-none bg-brandtint px-2.5 py-1 text-[11px] font-bold text-branddark">
                      {s.num}
                    </span>
                  </div>
                  <h3 className="mt-6 font-display text-xl font-bold tracking-[-0.01em] text-ink">
                    {s.title}
                  </h3>
                  <p className="mt-3 text-sm font-medium leading-relaxed text-ink2">{s.body}</p>
                </div>
              </FadeIn>
            ))}
          </div>
          <FadeIn delay={0.2} className="mt-12">
            <PrimaryBtn to="/contact" testid="partnerships-cta-lot">
              {c.cta.button}
            </PrimaryBtn>
          </FadeIn>
        </div>
      </section>

      <CtaBand title={c.cta.title} testid="partnerships-cta-band-lot" />
    </div>
  );
}
